import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Banner,
  Divider,
  Box,
  SkeletonBodyText,
} from '@shopify/polaris';
import { settingsService } from '../../services/settings.service';

export function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<'company' | 'quote' | 'email' | null>(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Company Settings
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');

  // Quote Settings
  const [quoteValidityDays, setQuoteValidityDays] = useState('30');
  const [quoteTerms, setQuoteTerms] = useState('');

  // Email Settings
  const [emailFrom, setEmailFrom] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const settings = await settingsService.getSettings();

        // Company
        setCompanyName(settings.company.name);
        setCompanyEmail(settings.company.email);
        setCompanyPhone(settings.company.phone);
        setCompanyAddress(settings.company.address);

        // Quote
        setQuoteValidityDays(String(settings.quote.validityDays));
        setQuoteTerms(settings.quote.terms);

        // Email
        setEmailFrom(settings.email.from);
        setEmailReplyTo(settings.email.replyTo);
      } catch (err) {
        console.error('Failed to load settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSaveCompanySettings = useCallback(async () => {
    try {
      setSaving('company');
      setError('');
      await settingsService.updateCompanySettings({
        name: companyName,
        email: companyEmail,
        phone: companyPhone,
        address: companyAddress,
      });
      setSuccess('Company settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save company settings');
      console.error(err);
    } finally {
      setSaving(null);
    }
  }, [companyName, companyEmail, companyPhone, companyAddress]);

  const handleSaveQuoteSettings = useCallback(async () => {
    try {
      setSaving('quote');
      setError('');
      await settingsService.updateQuoteSettings({
        validityDays: parseInt(quoteValidityDays) || 30,
        terms: quoteTerms,
      });
      setSuccess('Quote settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save quote settings');
      console.error(err);
    } finally {
      setSaving(null);
    }
  }, [quoteValidityDays, quoteTerms]);

  const handleSaveEmailSettings = useCallback(async () => {
    try {
      setSaving('email');
      setError('');
      await settingsService.updateEmailSettings({
        from: emailFrom,
        replyTo: emailReplyTo,
      });
      setSuccess('Email settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save email settings');
      console.error(err);
    } finally {
      setSaving(null);
    }
  }, [emailFrom, emailReplyTo]);

  if (loading) {
    return (
      <Page title="Settings">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <SkeletonBodyText lines={6} />
              </BlockStack>
            </Card>
          </Layout.Section>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <SkeletonBodyText lines={6} />
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Settings">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess('')}>
              {success}
            </Banner>
          </Layout.Section>
        )}

        {/* Company Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Company Information
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                This information will appear on quotes and communications
              </Text>

              <Divider />

              <TextField
                label="Company Name"
                value={companyName}
                onChange={setCompanyName}
                autoComplete="organization"
              />

              <TextField
                label="Email Address"
                type="email"
                value={companyEmail}
                onChange={setCompanyEmail}
                autoComplete="email"
              />

              <TextField
                label="Phone Number"
                value={companyPhone}
                onChange={setCompanyPhone}
                autoComplete="tel"
              />

              <TextField
                label="Address"
                value={companyAddress}
                onChange={setCompanyAddress}
                multiline={2}
                autoComplete="street-address"
              />

              <InlineStack align="end">
                <Button
                  variant="primary"
                  loading={saving === 'company'}
                  onClick={handleSaveCompanySettings}
                >
                  Save Company Settings
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quote Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Quote Settings
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                Configure default settings for customer quotes
              </Text>

              <Divider />

              <TextField
                label="Default Validity Period (days)"
                type="number"
                value={quoteValidityDays}
                onChange={setQuoteValidityDays}
                helpText="Number of days quotes remain valid"
                autoComplete="off"
              />

              <TextField
                label="Quote Terms & Conditions"
                value={quoteTerms}
                onChange={setQuoteTerms}
                multiline={6}
                autoComplete="off"
                helpText="These terms will appear on generated quote PDFs"
              />

              <InlineStack align="end">
                <Button
                  variant="primary"
                  loading={saving === 'quote'}
                  onClick={handleSaveQuoteSettings}
                >
                  Save Quote Settings
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Email Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Email Settings
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                Configure email sender information
              </Text>

              <Divider />

              <TextField
                label="From Email"
                type="email"
                value={emailFrom}
                onChange={setEmailFrom}
                autoComplete="off"
                helpText="Emails will be sent from this address"
              />

              <TextField
                label="Reply-To Email"
                type="email"
                value={emailReplyTo}
                onChange={setEmailReplyTo}
                autoComplete="off"
                helpText="Customer replies will go to this address"
              />

              <InlineStack align="end">
                <Button
                  variant="primary"
                  loading={saving === 'email'}
                  onClick={handleSaveEmailSettings}
                >
                  Save Email Settings
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Danger Zone */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd" tone="critical">
                Danger Zone
              </Text>

              <Divider />

              <Box
                padding="400"
                background="bg-surface-critical"
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      Clear All Cache
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      This will clear all cached data including catalogue PDFs
                    </Text>
                  </BlockStack>
                  <Button tone="critical">Clear Cache</Button>
                </InlineStack>
              </Box>

              <Box
                padding="400"
                background="bg-surface-critical"
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      Regenerate All Catalogues
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Force regeneration of all category catalogues
                    </Text>
                  </BlockStack>
                  <Button tone="critical">Regenerate All</Button>
                </InlineStack>
              </Box>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
